import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { getRequest, staticOptions } from "lib/core/apiClient";
import { GiftCardsResponseType, UseGiftCardsReturn } from "lib/types/giftCards";
import { getGiftCardsUrl, parseGiftCardsResponse } from "lib/utils/giftCards";
import { useMemo } from "react";
import useSWR from "swr";

/**
 * Gift-cards config for the current checkout.
 *
 * Keyed the same way discounts, rewards and payments are, so it goes out in that same tick
 * rather than waiting on a second round of state to settle. The endpoint is private-only —
 * same as payments — so where those two branch to a public fetcher for a guest, this one gates
 * on `isAuthenticated` instead: there is nothing to ask a public base for.
 *
 * `isAuthenticated` is deliberately the only auth half read here. `user.uid` is merged into the
 * user reducer and never cleared, so gating on it would keep the key alive after a logout and
 * keep firing the call for a session that is gone. The null key is also what refetches on login:
 * it flips with the auth state, and a key change is a fetch — no effect chases it.
 *
 * `mutatePayment` revalidates this key, so every cart, discount, rewards, shipping and address
 * change already refreshes it.
 */
const useGiftCards = (): UseGiftCardsReturn => {
  const {
    state: { checkoutId },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();

  const shouldFetchGiftCards = Boolean(checkoutId) && Boolean(isAuthenticated);
  const giftCardsUrl = getGiftCardsUrl(checkoutId);

  const {
    data: giftCardsResponse,
    isValidating,
    error,
    mutate: mutateGiftCards,
  } = useSWR<GiftCardsResponseType>(
    () => (shouldFetchGiftCards ? giftCardsUrl : null),
    (url: string) => getRequest(url, "KRATOS_PRIVATE"),
    staticOptions,
  );

  const giftCardConfig = useMemo(() => parseGiftCardsResponse(giftCardsResponse), [giftCardsResponse]);

  const isGiftCardsReady = !shouldFetchGiftCards || giftCardsResponse !== undefined || Boolean(error);

  const returnValue: UseGiftCardsReturn = {
    state: {
      giftCardConfig,
      isTileLoading: isValidating,
      isGiftCardsReady,
      error,
    },
    actions: {
      mutateGiftCards,
    },
  };

  return returnValue;
};

export default useGiftCards;
